import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Borders, Colors } from '../../constants'
import { COMPARATOR_INTERVAL_SECONDS } from '../../hooks'

const CIRCLE_RADIUS = 8
const DASH_ARRAY = 2 * Math.PI * CIRCLE_RADIUS
const WIDTH = 28

interface Props {
  timeoutStartAt: Date | null
  autoRefresh: boolean
  onClick: () => void
}

export function RefreshCountdown({ timeoutStartAt, autoRefresh, onClick }: Props) {
  const [dashOffset, setDashOffset] = useState(DASH_ARRAY)

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDashOffset(DASH_ARRAY * getElapsedPercentage(timeoutStartAt))
    }, 1000)
    return () => clearInterval(intervalId)
  }, [timeoutStartAt])

  return (
    <Button title={`Toggle auto refresh (${autoRefresh ? 'on' : 'off'})`} autoRefresh={autoRefresh} onClick={onClick}>
      <Svg viewBox={`0 0 ${WIDTH} ${WIDTH}`} stroke="currentColor">
        <BackgroundCircle r={CIRCLE_RADIUS} cx={WIDTH / 2} cy={WIDTH / 2}></BackgroundCircle>
        <AnimatedCircle r={CIRCLE_RADIUS} cx={WIDTH / 2} cy={WIDTH / 2} strokeDashoffset={dashOffset}></AnimatedCircle>
      </Svg>
    </Button>
  )
}

function getElapsedPercentage(timeoutStartAt: Date | null) {
  if (timeoutStartAt) {
    const elapsed = (new Date().getTime() - timeoutStartAt.getTime()) / 1000
    return elapsed / COMPARATOR_INTERVAL_SECONDS
  }

  return 1.0
}

const Button = styled.button.attrs((props: { autoRefresh: boolean }) => ({
  autoRefresh: props.autoRefresh,
}))`
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${WIDTH}px;
  height: ${WIDTH}px;
  color: ${Colors.Gray};
  background-color: ${(props) => (props.autoRefresh ? '#2f323a' : '#5a1829')};
  border: 0;
  border-radius: ${Borders.Radius};
  outline: 0;
  cursor: pointer;

  &:hover {
    color: ${Colors.White};
    background-color: ${Colors.Hover};

    svg {
      circle:last-child {
        stroke: ${Colors.White};
      }
    }
  }
`

const Svg = styled.svg`
  width: ${WIDTH}px;
  height: ${WIDTH}px;
  transform: scale(-1, 1) rotate(-90deg);

  circle {
    stroke-dasharray: ${DASH_ARRAY};
    stroke-linecap: round;
    stroke-width: 2px;
    fill: none;
  }
`

const AnimatedCircle = styled.circle`
  stroke: ${Colors.Hover};
  transition: stroke 0.5s;
`

const BackgroundCircle = styled.circle`
  stroke: #444;
`
